/**
 * Culling Evaluation Utilities
 *
 * Compares culling results against ground truth annotations to compute:
 * - RMSE for blur/focus scores
 * - RMSE for face scores
 * - Rejection accuracy for blur (at different thresholds)
 * - Rejection accuracy for face (at different thresholds)
 *
 * The dataset is stored in google drive: https://drive.google.com/drive/folders/1JwjcQ77XbK1f9273koIiS0_H3whU1c8U?dmr=1&ec=wgc-drive-%5Bmodule%5D-goto
 * input folder: culling-full-image-eval-raw; ground truth annotation file: annotation_human_corrected.csv
 *
 * To run the evaluation, put the annotation file in the public folder and uncomment the following line in culling-importing.ts:
 * void import("@/utils/culling-evaluation").then((m) => m.runAndLogEvaluation(true))
 * then import the data folder as a new project and enter culling mode.
 *
 * Note: this evaluation is for the overall blur and face scores of an image, not the individual face scores.
 */

import { store } from "@/store/store"
import { cullingInfoAtom, photosAtom } from "@/store/photo/photos-state"
import {
	BlurryRejectThreshold,
	FaceRejectThreshold,
	computeRMSE,
	computeMAE,
	computeRejectionMetrics,
	parseGroundTruthCSV,
	formatEvaluationResults,
} from "@/utils/culling-eval-metrics"
import type {
	GroundTruthEntry,
	EvaluationResult,
	RejectionMetrics,
	PhotoEvaluationDetail,
} from "@/utils/culling-eval-metrics"

export {
	computeRMSE,
	computeMAE,
	computeRejectionMetrics,
	parseGroundTruthCSV,
	formatEvaluationResults,
	BlurryRejectThreshold,
	FaceRejectThreshold,
}
export type { GroundTruthEntry, EvaluationResult, RejectionMetrics, PhotoEvaluationDetail }

/**
 * Ground truth cache - populated when evaluation runs.
 * Key: filename without extension
 * Other components can import and check this to display GT scores.
 */
export const groundTruthCache = new Map<string, { focus_score: number; face_score: number }>()

/**
 * Get ground truth for a photo by filename.
 * Returns null if evaluation hasn't run or no GT exists for this photo.
 */
export function getGroundTruth(
	fileName: string,
): { focus_score: number; face_score: number } | null {
	const lastDot = fileName.lastIndexOf(".")
	const nameWithoutExt = lastDot > 0 ? fileName.substring(0, lastDot) : fileName
	return groundTruthCache.get(nameWithoutExt) ?? null
}

/**
 * Get filename without extension for matching
 */
function getNameWithoutExtension(filename: string): string {
	const lastDot = filename.lastIndexOf(".")
	return lastDot > 0 ? filename.substring(0, lastDot) : filename
}

/**
 * Load ground truth from CSV file in public folder
 */
async function loadGroundTruth(): Promise<GroundTruthEntry[]> {
	const response = await fetch("/annotation_human_corrected.csv")
	if (!response.ok) {
		throw new Error(`Failed to load ground truth CSV: ${response.statusText}`)
	}
	const csvContent = await response.text()
	return parseGroundTruthCSV(csvContent)
}

/**
 * Run evaluation comparing culling results to ground truth
 */
export async function runCullingEvaluation(): Promise<EvaluationResult> {
	// Load ground truth - map by filename without extension
	const groundTruth = await loadGroundTruth()
	const gtMap = new Map(
		groundTruth.map((entry) => [getNameWithoutExtension(entry.filename), entry]),
	)

	// Populate the exported cache for other components to use
	groundTruthCache.clear()
	for (const [name, entry] of gtMap) {
		groundTruthCache.set(name, {
			focus_score: entry.focus_score,
			face_score: entry.face_score,
		})
	}

	// Get current culling info
	const cullingInfo = store.get(cullingInfoAtom)
	const photos = store.get(photosAtom)

	// Match photos to ground truth
	const details: PhotoEvaluationDetail[] = []
	const blurPredicted: number[] = []
	const blurActual: number[] = []
	const facePredicted: number[] = []
	const faceActual: number[] = []

	// For rejection accuracy
	const blurScoresPredicted: (number | null)[] = []
	const blurScoresGT: number[] = []
	const faceScoresPredicted: (number | null)[] = []
	const faceScoresGT: number[] = []

	let photosWithFaceGT = 0
	let photosWithFacePredicted = 0

	for (const [photoId, info] of Object.entries(cullingInfo)) {
		const photo = photos[photoId]
		if (!photo) continue

		// Match by filename without extension
		const gt = gtMap.get(getNameWithoutExtension(photo.fileName))
		if (!gt) {
			console.log("Culling Evaluation: no gt found for", photo.fileName)
			continue
		}

		const predictedBlur = info.scoreItem?.blur ?? null
		const predictedFace = info.scoreItem?.face ?? null
		const gtBlur = gt.focus_score
		const gtFace = gt.face_score

		// Blur comparison (always available in GT)
		blurScoresPredicted.push(predictedBlur)
		blurScoresGT.push(gtBlur)

		if (predictedBlur !== null) {
			blurPredicted.push(predictedBlur)
			blurActual.push(gtBlur)
		}

		// Face comparison (only when GT has face, i.e., face_score != -1)
		if (gtFace !== -1) {
			photosWithFaceGT++
			faceScoresPredicted.push(predictedFace)
			faceScoresGT.push(gtFace)

			if (predictedFace !== null) {
				photosWithFacePredicted++
				facePredicted.push(predictedFace)
				faceActual.push(gtFace)
			}
		}

		details.push({
			filename: photo.fileName,
			photoId,
			gtBlur,
			predictedBlur,
			gtFace,
			predictedFace,
			blurError: predictedBlur !== null ? predictedBlur - gtBlur : null,
			faceError: predictedFace !== null && gtFace !== -1 ? predictedFace - gtFace : null,
		})
	}

	// Compute RMSE and MAE
	const blurRMSE = computeRMSE(blurPredicted, blurActual)
	const faceRMSE = computeRMSE(facePredicted, faceActual)
	const blurMAE = computeMAE(blurPredicted, blurActual)
	const faceMAE = computeMAE(facePredicted, faceActual)

	// Compute rejection accuracy at different thresholds
	const blurThresholds = {
		Lenient: BlurryRejectThreshold.Lenient, // 1.5
		Balanced: BlurryRejectThreshold.Balanced, // 2.3
		Strict: BlurryRejectThreshold.Strict, // 3.2
	}

	const faceThresholds = {
		Lenient: FaceRejectThreshold.Lenient, // 1.5
		Balanced: FaceRejectThreshold.Balanced, // 2.3
		Strict: FaceRejectThreshold.Strict, // 3.2
	}

	const blurRejectionAccuracy: Record<string, RejectionMetrics> = {}
	for (const [name, threshold] of Object.entries(blurThresholds)) {
		blurRejectionAccuracy[name] = computeRejectionMetrics(
			blurScoresPredicted,
			blurScoresGT,
			threshold,
		)
	}

	const faceRejectionAccuracy: Record<string, RejectionMetrics> = {}
	for (const [name, threshold] of Object.entries(faceThresholds)) {
		// Only compute for photos that have face in ground truth
		faceRejectionAccuracy[name] = computeRejectionMetrics(
			faceScoresPredicted,
			faceScoresGT,
			threshold,
			5, // Default to 5 (no rejection) if face not detected
		)
	}

	return {
		blurRMSE,
		faceRMSE,
		blurMAE,
		faceMAE,
		blurRejectionAccuracy,
		faceRejectionAccuracy,
		totalPhotos: Object.keys(photos).length,
		matchedPhotos: details.length,
		photosWithFaceGT,
		photosWithFacePredicted,
		details,
	}
}

// Guard to prevent multiple exports
let isExporting = false

/**
 * Export summary metrics to TXT file
 */
function exportSummaryToTxt(result: EvaluationResult): void {
	const content = formatEvaluationResults(result)
	const blob = new Blob([content], { type: "text/plain;charset=utf-8;" })
	const link = document.createElement("a")
	link.href = URL.createObjectURL(blob)
	link.download = `culling_evaluation_summary_${new Date().toISOString().slice(0, 10)}.txt`
	link.click()
}

/**
 * Export per-photo details to CSV
 */
function exportDetailsToCSV(result: EvaluationResult): void {
	const headers = [
		"filename",
		"photoId",
		"gt_blur",
		"predicted_blur",
		"blur_error",
		"gt_face",
		"predicted_face",
		"face_error",
	]

	const rows = result.details.map((d) =>
		[
			d.filename,
			d.photoId,
			d.gtBlur,
			d.predictedBlur ?? "",
			d.blurError?.toFixed(2) ?? "",
			d.gtFace,
			d.predictedFace ?? "",
			d.faceError?.toFixed(2) ?? "",
		].join(","),
	)

	const csvContent = [headers.join(","), ...rows].join("\n")
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
	const link = document.createElement("a")
	link.href = URL.createObjectURL(blob)
	link.download = `culling_evaluation_details_${new Date().toISOString().slice(0, 10)}.csv`
	link.click()
}

/**
 * Export both summary (TXT) and details (CSV)
 */
export function exportResults(result: EvaluationResult): void {
	if (isExporting) {
		console.log("Export already in progress, skipping duplicate...")
		return
	}
	isExporting = true

	exportSummaryToTxt(result)
	exportDetailsToCSV(result)

	// Reset guard after a short delay
	setTimeout(() => {
		isExporting = false
	}, 1000)
}

/**
 * Run evaluation, log results, and optionally export to CSV
 * Call this from browser console: window.runCullingEvaluation()
 * For dataset details, see the comment at the beginning of this file.
 */
export async function runAndLogEvaluation(
	exportCSV: boolean = false,
): Promise<EvaluationResult | null> {
	// Safety check 1: Only run in dev environment
	if (!import.meta.env.DEV) {
		return null
	}

	// Safety check 2: Only run if all photos have culling scores - shouldn't happen now that the duplicate scheduling bug is fixed
	const cullingInfo = store.get(cullingInfoAtom)
	const photos = store.get(photosAtom)
	const photoCount = Object.keys(photos).length
	const cullingInfoWithScoresCount = Object.values(cullingInfo).filter(
		(info) => info.scoreItem != null,
	).length

	if (cullingInfoWithScoresCount < photoCount) {
		console.log(
			`Culling evaluation skipped: only ${cullingInfoWithScoresCount}/${photoCount} photos have culling scores`,
		)
		return null
	}

	console.log("Starting culling evaluation...")
	const result = await runCullingEvaluation()
	console.log(formatEvaluationResults(result))

	if (exportCSV) {
		exportResults(result)
		console.log("📁 Results exported: summary.txt + details.csv")
	}

	return result
}
